import { toNullableText } from "../careersFormat";
import { newRowKey } from "../jobs/jobForm";
import type { CareersWorkHistoryEntry } from "../types";

/**
 * A person's work history, as every Careers surface reads and edits it: the CV review card and Edit details (as
 * form rows), the profile and the board's ⋯ menu (as labels), and the board / table search (as text to match).
 * Framework-free, so the rules are tested on their own.
 */

/** The backend's bounds (models.careers MIN_WORK_YEAR / MAX_WORK_HISTORY_ENTRIES). */
export const WORK_HISTORY_LIMITS = { MIN_YEAR: 1950, MAX_ENTRIES: 20 } as const;

/** One job as a form holds it: every value a string ("" = not known), plus a stable key for the row list. */
export type WorkHistoryRow = { key: string; title: string; employer: string; startYear: string; endYear: string; isCurrent: boolean };

export type WorkHistoryProblem = "title_required" | "year_invalid" | "end_before_start" | "year_in_future";

export const WORK_HISTORY_PROBLEM_KEY: Record<WorkHistoryProblem, string> = {
	title_required: "careers.work_history.problem.title_required",
	year_invalid: "careers.work_history.problem.year_invalid",
	end_before_start: "careers.work_history.problem.end_before_start",
	year_in_future: "careers.work_history.problem.year_in_future",
};

// DEFAULT: an unknown value is an EMPTY field — that is how a form shows "not known" — and it goes back as null.
const yearText = (year: number | null): string => (year === null ? "" : String(year));

export const workHistoryRowsFrom = (entries: CareersWorkHistoryEntry[]): WorkHistoryRow[] =>
	entries.map((entry) => ({
		key: newRowKey(),
		title: entry.title,
		employer: entry.employer ?? "",
		startYear: yearText(entry.start_year),
		endYear: yearText(entry.end_year),
		isCurrent: entry.is_current,
	}));

export const blankWorkHistoryRow = (): WorkHistoryRow => ({ key: newRowKey(), title: "", employer: "", startYear: "", endYear: "", isCurrent: false });

/** "2021" → 2021, "" → null, anything that isn't a four-digit year from 1950 on → "invalid". */
const parseYear = (text: string): number | null | "invalid" => {
	const trimmed = text.trim();
	if (trimmed === "") return null;
	if (!/^\d{4}$/.test(trimmed)) return "invalid";
	const year = Number(trimmed);
	return year < WORK_HISTORY_LIMITS.MIN_YEAR ? "invalid" : year;
};

type RowCheck = { ok: true; entry: CareersWorkHistoryEntry } | { ok: false; problem: WorkHistoryProblem };

/** One row as the request sends it, or why it can't be sent yet. A "now" job sends no end year. */
const checkRow = (row: WorkHistoryRow, thisYear: number): RowCheck => {
	const title = toNullableText(row.title);
	if (title === null) return { ok: false, problem: "title_required" };
	const start = parseYear(row.startYear);
	const end = row.isCurrent ? null : parseYear(row.endYear);
	if (start === "invalid" || end === "invalid") return { ok: false, problem: "year_invalid" };
	if ((start !== null && start > thisYear) || (end !== null && end > thisYear)) return { ok: false, problem: "year_in_future" };
	if (start !== null && end !== null && end < start) return { ok: false, problem: "end_before_start" };
	return { ok: true, entry: { title, employer: toNullableText(row.employer), start_year: start, end_year: end, is_current: row.isCurrent } };
};

/** Each row's problem, by row key — shown under that row. Rows with none are absent. */
export const workHistoryProblems = (rows: WorkHistoryRow[], thisYear: number): Partial<Record<string, WorkHistoryProblem>> =>
	Object.fromEntries(
		rows.flatMap((row) => {
			const check = checkRow(row, thisYear);
			return check.ok ? [] : [[row.key, check.problem]];
		}),
	);

export type WorkHistoryCheck = { ok: true; entries: CareersWorkHistoryEntry[] } | { ok: false; problem: WorkHistoryProblem };

/** The rows as the request sends them, in their order, or the first row's reason they can't be sent. */
export const workHistoryFromRows = (rows: WorkHistoryRow[], thisYear: number): WorkHistoryCheck => {
	const entries: CareersWorkHistoryEntry[] = [];
	for (const row of rows) {
		const check = checkRow(row, thisYear);
		if (!check.ok) return check;
		entries.push(check.entry);
	}
	return { ok: true, entries };
};

/** Two histories say the same thing — same jobs, same order. */
export const sameWorkHistory = (history: CareersWorkHistoryEntry[], otherHistory: CareersWorkHistoryEntry[]): boolean =>
	history.length === otherHistory.length &&
	history.every(
		(entry, index) =>
			entry.title === otherHistory[index].title &&
			entry.employer === otherHistory[index].employer &&
			entry.start_year === otherHistory[index].start_year &&
			entry.end_year === otherHistory[index].end_year &&
			entry.is_current === otherHistory[index].is_current,
	);

/** An i18n key with its params — the caller translates. */
export type WorkHistoryLabel = { key: string; params: Record<string, string | number> };

/**
 * "2021 – now", "2018 – 2021", "From 2019", "Until 2016" — or null when the job has no years at all (the line then
 * shows just the workplace).
 */
export const workYearsLabel = (entry: CareersWorkHistoryEntry): WorkHistoryLabel | null => {
	const { start_year: start, end_year: end, is_current: current } = entry;
	if (start !== null && current) return { key: "careers.work_history.years_now", params: { start } };
	if (start !== null && end !== null)
		return start === end
			? { key: "careers.work_history.year_single", params: { year: start } }
			: { key: "careers.work_history.years_range", params: { start, end } };
	if (start !== null) return { key: "careers.work_history.years_from", params: { start } };
	if (end !== null) return { key: "careers.work_history.years_until", params: { end } };
	return current ? { key: "careers.work_history.now_only", params: {} } : null;
};

/**
 * How long they were there, in whole years ("3 years", "Under a year") — only when both ends are known (a "now"
 * job ends this year). Year-only dates can't say more than that.
 */
export const workTenureLabel = (entry: CareersWorkHistoryEntry, thisYear: number): WorkHistoryLabel | null => {
	const end = entry.is_current ? thisYear : entry.end_year;
	if (entry.start_year === null || end === null) return null;
	const years = end - entry.start_year;
	return years < 1 ? { key: "careers.work_history.tenure_under_year", params: {} } : { key: "careers.work_history.tenure_years", params: { count: years } };
};

type YearSpan = { start: number; end: number };

/** A row's years as a span — a job they still have runs to this year — or null when either end isn't a valid year. */
const spanOf = (row: WorkHistoryRow, thisYear: number): YearSpan | null => {
	const start = parseYear(row.startYear);
	const end = row.isCurrent ? thisYear : parseYear(row.endYear);
	if (typeof start !== "number" || typeof end !== "number" || end < start) return null;
	return { start, end };
};

/**
 * The years of experience the jobs add up to, in whole years like each job's tenure: every job from its start
 * year to its end, jobs that overlap or meet counted once, breaks between jobs left out. 2020–2021, 2021–now
 * and 2023–2025 → 2020–2026 → 6. Null when no job has both ends — the jobs then say nothing about it.
 */
export const yearsOfExperienceFromRows = (rows: WorkHistoryRow[], thisYear: number): number | null => {
	const spans = rows
		.flatMap((row) => {
			const span = spanOf(row, thisYear);
			return span === null ? [] : [span];
		})
		.sort((first, second) => first.start - second.start);
	if (spans.length === 0) return null;
	return spans
		.reduce<YearSpan[]>((runs, span) => {
			// Sorted by start, so a span overlaps or meets only the run before it.
			const last = runs[runs.length - 1];
			if (last !== undefined && span.start <= last.end) last.end = Math.max(last.end, span.end);
			else runs.push({ ...span });
			return runs;
		}, [])
		.reduce((total, run) => total + (run.end - run.start), 0);
};

/**
 * A form's jobs replaced by `rows`, with Years of experience following them: when the change moves what the
 * jobs add up to (a year edited, a job added or removed, "Now" ticked), the field takes the new total. A change
 * that doesn't (a title, a workplace, a reorder) leaves the field as it is — a CV's "5.1" or the reviewer's own
 * figure stays until the jobs actually say something else.
 */
export const withWorkHistoryRows = <Draft extends { fields: { years_experience: string }; workHistory: WorkHistoryRow[] }>(
	draft: Draft,
	rows: WorkHistoryRow[],
	thisYear: number,
): Draft => {
	const years = yearsOfExperienceFromRows(rows, thisYear);
	return years === null || years === yearsOfExperienceFromRows(draft.workHistory, thisYear)
		? { ...draft, workHistory: rows }
		: { ...draft, workHistory: rows, fields: { ...draft.fields, years_experience: String(years) } };
};

/** The first job whose title or workplace contains `needle` (already lower-cased and trimmed), for search. */
export const matchingWorkHistoryEntry = (entries: CareersWorkHistoryEntry[], needle: string): CareersWorkHistoryEntry | undefined =>
	entries.find((entry) => [entry.title, entry.employer].some((text) => text?.toLocaleLowerCase().includes(needle)));
